import { db, COLLECTIONS } from '../firebase/db';
import { collection, doc, getDoc, query, where, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { Device, DesiredState, AppliedState, DeviceHealth } from '../types';

// Read desired state (policy sent by parent)
export async function getDesiredState(familyId: string, deviceId: string): Promise<DesiredState | null> {
  try {
    const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'desiredState', 'current');
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return snap.data() as DesiredState;
  } catch (err) {
    console.error('Failed to read desired state:', err);
    return null;
  }
}

// Read applied state (policy device is actually enforcing)
export async function getAppliedState(familyId: string, deviceId: string): Promise<AppliedState | null> {
  try {
    const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'reportedState', 'current');
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return snap.data() as AppliedState;
  } catch (err) {
    console.error('Failed to read applied state:', err);
    return null;
  }
}

// Listen to applied state changes in real-time
export function onAppliedStateChange(
  familyId: string,
  deviceId: string,
  callback: (state: AppliedState | null) => void
): Unsubscribe {
  const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'reportedState', 'current');
  return onSnapshot(ref, snap => {
    callback(snap.exists() ? (snap.data() as AppliedState) : null);
  });
}

// Calculate device health summary
export function calculateDeviceHealth(
  device: Device,
  desired: DesiredState | null,
  applied: AppliedState | null
): DeviceHealth {
  const warnings: string[] = [];
  let complianceStatus: 'IN_SYNC' | 'STALE' | 'ERROR' | 'UNKNOWN' = 'UNKNOWN';

  if (!desired || !applied) {
    complianceStatus = 'UNKNOWN';
    if (!desired) warnings.push('No desired policy set');
    if (!applied) warnings.push('Device has not reported state');
  } else {
    const versionMatch = desired.version === applied.appliedVersion;
    const now = Date.now();
    const timeSinceApplied = now - (applied.timestamp?.getTime?.() || 0);
    const isStale = timeSinceApplied > 6 * 60 * 60 * 1000; // 6 hours

    if (versionMatch && !isStale) {
      complianceStatus = 'IN_SYNC';
    } else if (versionMatch && isStale) {
      complianceStatus = 'STALE';
      warnings.push(`Applied 6+ hours ago (${formatTimeSince(timeSinceApplied)})`);
    } else {
      complianceStatus = 'STALE';
      warnings.push(`Applied policy v${applied.appliedVersion}, desired v${desired.version}`);
    }
  }

  // Check device online status
  const now = Date.now();
  const lastSeenMs = device.lastSeen?.getTime?.() || 0;
  const offlineMinutes = Math.floor((now - lastSeenMs) / 60000);

  if (offlineMinutes > 360) {
    // 6+ hours offline
    warnings.push(`Offline for ${Math.floor(offlineMinutes / 60)}h — policy changes may not apply`);
  } else if (offlineMinutes > 60) {
    warnings.push(`Offline for ${offlineMinutes}m`);
  }

  return {
    device,
    desired: desired || null,
    applied: applied || null,
    connectivity: {
      status: offlineMinutes < 10 ? 'ONLINE' : 'OFFLINE',
      lastSeen: device.lastSeen || new Date(),
    },
    warnings,
  };
}

function formatTimeSince(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return 'just now';
}
